import { getLocaleString } from '@assets/i18n/config';
import Decimal from 'decimal.js';

export const AllocationUnits = {
  PERCENT: 'PERCENT',
  DOLLAR: 'DOLLAR',
} as const;

export type AllocationUnit =
  (typeof AllocationUnits)[keyof typeof AllocationUnits];

/** Display precision for portfolio allocation % and $ inputs (matches AmountField default). */
export const ALLOCATION_DISPLAY_DECIMAL_PLACES = 2;

/** Formats allocation dollar cents for read-only / display (handles $0.00 correctly). */
export const formatAllocationAmountDisplay = (
  amountCents: number,
  decimalPlaces = ALLOCATION_DISPLAY_DECIMAL_PLACES,
): string => {
  const dollars = toDecimal(amountCents).div(100);
  const value = dollars.isFinite() ? dollars.toNumber() : 0;

  return new Intl.NumberFormat(getLocaleString(), {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);
};

/** R8: manual percent entry is capped at 2 display decimals -> 4 fractional wire digits. */
export const MANUAL_PERCENT_WIRE_DECIMALS = 4;
/** R8: amount-derived / post-Allocate fractions allow up to 10 fractional wire digits. */
export const AMOUNT_DERIVED_WIRE_DECIMALS = 10;

/** Pre-10-decimal BE rows; display inversion still matches cents at this precision. */
const LEGACY_AMOUNT_DERIVED_WIRE_DECIMALS = 6;

/** Cents search radius for wire encode/decode near a target (half of one wire ulp). */
const wireCentSearchHalfWidth = (mvCents: number) =>
  Math.ceil(mvCents / (2 * 10 ** AMOUNT_DERIVED_WIRE_DECIMALS)) + 5;

/**
 * Wider search for display inversion of legacy 6-decimal persisted fractions
 * until all rows are re-saved at 10 wire decimals.
 */
const legacyWireDisplaySearchHalfWidth = (mvCents: number) =>
  Math.ceil(mvCents / (2 * 10 ** 6)) + 1;

const toDecimal = (value: string | number | null | undefined): Decimal => {
  if (value === '' || value === null || value === undefined)
    return new Decimal(0);
  const d = new Decimal(`${value}`);
  return d.isFinite() ? d : new Decimal(0);
};

// holding-service rounds `customExpectedPercentage` to 10 decimals on receipt, so
// sending more only ships float noise (e.g. 0.20972571124999997).
export const roundToWireDecimals = (fraction: number) =>
  toDecimal(fraction).toDecimalPlaces(AMOUNT_DERIVED_WIRE_DECIMALS).toNumber();

/** "55.55" (display %) -> 0.5555 fraction, capped at 4 wire decimals. */
export const displayPercentToFraction = (
  displayPercent: string | number | null | undefined,
): number =>
  toDecimal(displayPercent)
    .div(100)
    .toDecimalPlaces(MANUAL_PERCENT_WIRE_DECIMALS)
    .toNumber();

/** 0.5555 fraction -> 55.55 display percent number (for seeding the % input). */
export const fractionToDisplayPercent = (
  fraction: number,
  decimals = ALLOCATION_DISPLAY_DECIMAL_PLACES,
): number =>
  toDecimal(fraction).times(100).toDecimalPlaces(decimals).toNumber();

/** amountCents / mvCents -> fraction, capped at 10 wire decimals. 0 when mvCents falsy. */
export const amountCentsToFraction = (
  amountCents: number,
  mvCents: number,
): number => {
  if (!mvCents || !Number.isFinite(amountCents)) return 0;
  return toDecimal(amountCents)
    .div(mvCents)
    .toDecimalPlaces(AMOUNT_DERIVED_WIRE_DECIMALS)
    .toNumber();
};

/**
 * Fraction for manual dollar entry where fractionToAmountCents reproduces whole cents.
 * Uses up to 15 fractional digits when the 10-decimal wire cap alone would drift.
 */
export const fractionFromAmountCents = (
  amountCents: number,
  mvCents: number,
): number => {
  if (!mvCents || !Number.isFinite(amountCents)) return 0;
  const targetCents = Math.round(amountCents);
  if (targetCents === 0) return 0;

  const exact = toDecimal(targetCents).div(mvCents);

  let best = amountCentsToFraction(targetCents, mvCents);
  for (
    let decimals = AMOUNT_DERIVED_WIRE_DECIMALS;
    decimals <= 15;
    decimals++
  ) {
    const candidate = exact
      .toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP)
      .toNumber();
    if (fractionToAmountCents(candidate, mvCents) === targetCents) {
      best = candidate;
    }
  }

  return best;
};

/** fraction * mvCents -> whole cents. 0 when mvCents falsy. */
export const fractionToAmountCents = (
  fraction: number,
  mvCents: number,
): number => {
  if (!mvCents || !Number.isFinite(fraction)) return 0;
  return toDecimal(fraction).times(mvCents).round().toNumber();
};

/** Cents the backend derives from a stored wire fraction (truncated to wire decimals). */
export const wireFractionToAmountCents = (
  fraction: number,
  mvCents: number,
): number => {
  if (!mvCents || !Number.isFinite(fraction)) return 0;
  const wireFraction = toDecimal(fraction)
    .toDecimalPlaces(AMOUNT_DERIVED_WIRE_DECIMALS)
    .toNumber();
  return fractionToAmountCents(wireFraction, mvCents);
};

/** Wire fraction whose BE-derived cents equal targetWireCents when possible. */
export const wireFractionForWireAmountCents = (
  targetWireCents: number,
  mvCents: number,
): number => {
  if (!mvCents || !Number.isFinite(targetWireCents)) return 0;

  const direct = amountCentsToFraction(targetWireCents, mvCents);
  if (wireFractionToAmountCents(direct, mvCents) === targetWireCents) {
    return direct;
  }

  const halfWidth = wireCentSearchHalfWidth(mvCents);
  for (
    let candidate = targetWireCents - halfWidth;
    candidate <= targetWireCents + halfWidth;
    candidate++
  ) {
    if (candidate < 0) continue;
    const fraction = amountCentsToFraction(candidate, mvCents);
    if (wireFractionToAmountCents(fraction, mvCents) === targetWireCents) {
      return fraction;
    }
  }

  return direct;
};

export type WireColumnEntry = {
  financialProductId: string;
  amountCents: number;
};

/**
 * Per-account save fractions that pass BE validation: each uses wire decimals and
 * the column sums to mvCents × securityMassFraction in BE-derived cents.
 */
export const canonicalizeWireColumnFractions = (
  entries: WireColumnEntry[],
  mvCents: number,
  securityMassFraction: number,
): Map<string, number> => {
  const wireFractions = new Map<string, number>();
  if (entries.length === 0 || !mvCents) return wireFractions;

  const targetColCents = Math.round(mvCents * securityMassFraction);
  const amounts = entries.map((entry) => ({
    financialProductId: entry.financialProductId,
    cents: Math.round(entry.amountCents),
  }));

  const totalCents = amounts.reduce((sum, entry) => sum + entry.cents, 0);
  const remainder = targetColCents - totalCents;
  if (remainder !== 0) {
    amounts[amounts.length - 1]!.cents += remainder;
  }

  let wireSum = 0;
  for (let index = 0; index < amounts.length - 1; index++) {
    const { financialProductId, cents } = amounts[index]!;
    const fraction = amountCentsToFraction(cents, mvCents);
    wireFractions.set(financialProductId, fraction);
    wireSum += wireFractionToAmountCents(fraction, mvCents);
  }

  const last = amounts[amounts.length - 1]!;
  const lastWireCents = targetColCents - wireSum;
  wireFractions.set(
    last.financialProductId,
    wireFractionForWireAmountCents(lastWireCents, mvCents),
  );

  return wireFractions;
};

export type WireMatrixCell = {
  key: string;
  subAccountId: string;
  financialProductId: string;
  amountCents: number;
  mvCents: number;
};

type WireMatrixEntry = {
  fraction: number;
  mvCents: number;
  subAccountId: string;
  financialProductId: string;
};

const reconcileIntegerMarginals = (
  amountCentsByKey: Map<string, number>,
  cells: WireMatrixCell[],
  rowTargetCents: ReadonlyMap<string, number>,
  colTargetCents: ReadonlyMap<string, number>,
) => {
  const fps = [...new Set(cells.map((cell) => cell.financialProductId))];
  const sas = [...new Set(cells.map((cell) => cell.subAccountId))];

  const applyRowRemainders = () => {
    for (const financialProductId of fps) {
      const keys = cells
        .filter((cell) => cell.financialProductId === financialProductId)
        .map((cell) => cell.key);
      const target = rowTargetCents.get(financialProductId) ?? 0;
      const actual = keys.reduce(
        (sum, key) => sum + (amountCentsByKey.get(key) ?? 0),
        0,
      );
      const remainder = target - actual;
      if (remainder === 0 || keys.length === 0) continue;
      const key = keys[keys.length - 1]!;
      amountCentsByKey.set(key, (amountCentsByKey.get(key) ?? 0) + remainder);
    }
  };

  const applyColRemainders = () => {
    for (const subAccountId of sas) {
      const keys = cells
        .filter((cell) => cell.subAccountId === subAccountId)
        .map((cell) => cell.key);
      const target = colTargetCents.get(subAccountId) ?? 0;
      const actual = keys.reduce(
        (sum, key) => sum + (amountCentsByKey.get(key) ?? 0),
        0,
      );
      const remainder = target - actual;
      if (remainder === 0 || keys.length === 0) continue;
      const key = keys[keys.length - 1]!;
      amountCentsByKey.set(key, (amountCentsByKey.get(key) ?? 0) + remainder);
    }
  };

  applyRowRemainders();
  applyColRemainders();
  applyRowRemainders();
};

const wireCentsOfEntry = (entry: WireMatrixEntry) =>
  wireFractionToAmountCents(entry.fraction, entry.mvCents);

const distributeWireRemainder = (
  keys: string[],
  matrix: Map<string, WireMatrixEntry>,
  remainder: number,
): number => {
  if (remainder === 0 || keys.length === 0) return remainder;

  let left = remainder;
  for (const key of [...keys].reverse()) {
    if (left === 0) break;
    const entry = matrix.get(key);
    if (!entry) continue;
    const current = wireCentsOfEntry(entry);
    entry.fraction = wireFractionForWireAmountCents(
      current + left,
      entry.mvCents,
    );
    left -= wireCentsOfEntry(entry) - current;
  }

  if (left === 0) return 0;

  const direction = left > 0 ? 1 : -1;
  const maxPasses = Math.abs(left) * keys.length * 2;
  for (let pass = 0; pass < maxPasses && left !== 0; pass++) {
    const key = keys[pass % keys.length]!;
    const entry = matrix.get(key);
    if (!entry) continue;
    const current = wireCentsOfEntry(entry);
    entry.fraction = wireFractionForWireAmountCents(
      current + direction,
      entry.mvCents,
    );
    const next = wireCentsOfEntry(entry);
    if (next === current) continue;
    left -= next - current;
  }

  return left;
};

const achievableWireCentsNear = (
  mvCents: number,
  centerCents: number,
  span = 40,
): number[] => {
  const values = new Set<number>();
  for (
    let candidate = centerCents - span;
    candidate <= centerCents + span;
    candidate++
  ) {
    if (candidate < 0) continue;
    values.add(
      wireFractionToAmountCents(
        amountCentsToFraction(candidate, mvCents),
        mvCents,
      ),
    );
  }
  return [...values];
};

/**
 * Exhaustive search for a wire-cent combination that hits `targetRowCents` exactly.
 * Intended for household rows of ≤ ~8 members; each cell has ≤ ~81 achievable cent
 * options (`achievableWireCentsNear` span=40). Pruned by remainingBounds, but still
 * worst-case exponential — abort after FIX_ROW_EXACT_MAX_VISITS and let the caller
 * fall back to `distributeWireRemainder`.
 */
const FIX_ROW_EXACT_MAX_VISITS = 50_000;

const fixRowExact = (
  keys: string[],
  matrix: Map<string, WireMatrixEntry>,
  targetRowCents: number,
): boolean => {
  const entries = keys
    .map((key) => matrix.get(key))
    .filter((entry): entry is WireMatrixEntry => entry !== undefined);
  if (entries.length !== keys.length) return false;

  const optionsByIndex = entries.map((entry) =>
    achievableWireCentsNear(entry.mvCents, wireCentsOfEntry(entry)),
  );

  const remainingBounds = (startIndex: number) => {
    let min = 0;
    let max = 0;
    for (let index = startIndex; index < optionsByIndex.length; index++) {
      const options = optionsByIndex[index]!;
      if (options.length === 0) return { min: 0, max: 0 };
      min += Math.min(...options);
      max += Math.max(...options);
    }
    return { min, max };
  };

  let visits = 0;
  const assign = (index: number, sum: number): boolean => {
    if (++visits > FIX_ROW_EXACT_MAX_VISITS) return false;

    if (index === entries.length - 1) {
      const entry = entries[index]!;
      const needed = targetRowCents - sum;
      if (!optionsByIndex[index]!.includes(needed)) return false;
      entry.fraction = wireFractionForWireAmountCents(needed, entry.mvCents);
      return wireCentsOfEntry(entry) === needed;
    }

    const { min: remainingMin, max: remainingMax } = remainingBounds(index + 1);

    for (const cents of optionsByIndex[index]!) {
      const entry = entries[index]!;
      entry.fraction = wireFractionForWireAmountCents(cents, entry.mvCents);
      const actual = wireCentsOfEntry(entry);
      const nextSum = sum + actual;
      if (nextSum + remainingMax < targetRowCents) continue;
      if (nextSum + remainingMin > targetRowCents) continue;
      if (assign(index + 1, nextSum)) return true;
    }

    return false;
  };

  return assign(0, 0);
};

/**
 * Household matrix save fractions: wire decimals per cell with BE-valid column
 * and row marginals (AMOUNT_SUM_OUT_OF_RANGE + AGGREGATE_CONSERVATION).
 */
export const canonicalizeWireMatrixFractions = (
  cells: WireMatrixCell[],
  rowTargetCents: ReadonlyMap<string, number>,
  colTargetCents: ReadonlyMap<string, number>,
  maxIterations = 32,
): Map<string, number> => {
  const wireFractions = new Map<string, number>();
  if (cells.length === 0) return wireFractions;

  const amountCentsByKey = new Map<string, number>();
  for (const cell of cells) {
    amountCentsByKey.set(cell.key, Math.round(cell.amountCents));
  }

  reconcileIntegerMarginals(
    amountCentsByKey,
    cells,
    rowTargetCents,
    colTargetCents,
  );

  const sas = [...new Set(cells.map((cell) => cell.subAccountId))];
  for (const subAccountId of sas) {
    const saCells = cells.filter((cell) => cell.subAccountId === subAccountId);
    const mvCents = saCells[0]?.mvCents ?? 0;
    const colTarget = colTargetCents.get(subAccountId) ?? 0;
    const colWire = canonicalizeWireColumnFractions(
      saCells.map((cell) => ({
        financialProductId: cell.financialProductId,
        amountCents: amountCentsByKey.get(cell.key) ?? 0,
      })),
      mvCents,
      colTarget / mvCents,
    );
    for (const cell of saCells) {
      const fraction = colWire.get(cell.financialProductId);
      if (fraction !== undefined) {
        wireFractions.set(cell.key, fraction);
      }
    }
  }

  const matrix = new Map<string, WireMatrixEntry>();
  for (const cell of cells) {
    const fraction =
      wireFractions.get(cell.key) ??
      amountCentsToFraction(amountCentsByKey.get(cell.key) ?? 0, cell.mvCents);
    matrix.set(cell.key, {
      fraction,
      mvCents: cell.mvCents,
      subAccountId: cell.subAccountId,
      financialProductId: cell.financialProductId,
    });
  }

  const fps = [...new Set(cells.map((cell) => cell.financialProductId))];

  const applyRowRemainders = () => {
    for (const financialProductId of fps) {
      const rowCells = cells.filter(
        (cell) => cell.financialProductId === financialProductId,
      );
      const keys = rowCells.map((cell) => cell.key);
      const target = rowTargetCents.get(financialProductId) ?? 0;
      const actual = keys.reduce((sum, key) => {
        const entry = matrix.get(key);
        return entry ? sum + wireCentsOfEntry(entry) : sum;
      }, 0);
      const remainder = target - actual;
      if (remainder === 0 || keys.length === 0) continue;

      if (fixRowExact(keys, matrix, target)) {
        continue;
      }

      distributeWireRemainder(
        [...rowCells]
          .sort((a, b) => b.mvCents - a.mvCents)
          .map((cell) => cell.key),
        matrix,
        remainder,
      );
    }
  };

  const applyColRemainders = () => {
    for (const subAccountId of sas) {
      const keys = cells
        .filter((cell) => cell.subAccountId === subAccountId)
        .map((cell) => cell.key);
      const target = colTargetCents.get(subAccountId) ?? 0;
      const actual = keys.reduce((sum, key) => {
        const entry = matrix.get(key);
        return entry ? sum + wireCentsOfEntry(entry) : sum;
      }, 0);
      const remainder = target - actual;
      if (remainder === 0 || keys.length === 0) continue;
      distributeWireRemainder(keys, matrix, remainder);
    }
  };

  const isBalanced = () => {
    for (const financialProductId of fps) {
      const keys = cells
        .filter((cell) => cell.financialProductId === financialProductId)
        .map((cell) => cell.key);
      const target = rowTargetCents.get(financialProductId) ?? 0;
      const actual = keys.reduce((sum, key) => {
        const entry = matrix.get(key);
        return entry ? sum + wireCentsOfEntry(entry) : sum;
      }, 0);
      if (actual !== target) return false;
    }
    for (const subAccountId of sas) {
      const keys = cells
        .filter((cell) => cell.subAccountId === subAccountId)
        .map((cell) => cell.key);
      const target = colTargetCents.get(subAccountId) ?? 0;
      const actual = keys.reduce((sum, key) => {
        const entry = matrix.get(key);
        return entry ? sum + wireCentsOfEntry(entry) : sum;
      }, 0);
      if (actual !== target) return false;
    }
    return true;
  };

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    applyRowRemainders();
    applyColRemainders();
    applyRowRemainders();
    if (isBalanced()) break;
  }

  for (const [key, entry] of matrix) {
    wireFractions.set(key, entry.fraction);
  }

  return wireFractions;
};

/**
 * Dollar display cents for a persisted wire fraction.
 * BE stores wire-truncated fractions; naive fraction×MV can drift from the entered
 * dollar amount (legacy 6-decimal rows need a wider cent search — see legacy helper).
 */
export const displayAmountCentsFromFraction = (
  fraction: number,
  mvCents: number,
): number => {
  if (!mvCents || !Number.isFinite(fraction)) return 0;

  const fractionDecimal = toDecimal(fraction);
  const isLegacySixDecimalWire = fractionDecimal.eq(
    fractionDecimal.toDecimalPlaces(LEGACY_AMOUNT_DERIVED_WIRE_DECIMALS),
  );
  const wireDecimals = isLegacySixDecimalWire
    ? LEGACY_AMOUNT_DERIVED_WIRE_DECIMALS
    : AMOUNT_DERIVED_WIRE_DECIMALS;
  const wireDecimal = fractionDecimal.toDecimalPlaces(wireDecimals);
  const wireFraction = wireDecimal.toNumber();

  const centsToWireFraction = (cents: number) =>
    toDecimal(cents).div(mvCents).toDecimalPlaces(wireDecimals).toNumber();

  if (!isLegacySixDecimalWire && !wireDecimal.eq(fractionDecimal)) {
    return fractionToAmountCents(fraction, mvCents);
  }

  const naive = fractionToAmountCents(wireFraction, mvCents);
  const exactCenter = wireDecimal.times(mvCents).toNumber();
  const halfWidth = isLegacySixDecimalWire
    ? legacyWireDisplaySearchHalfWidth(mvCents)
    : Math.ceil(mvCents / (2 * 10 ** wireDecimals)) + 1;

  const candidates: number[] = [];
  for (
    let candidate = naive - halfWidth;
    candidate <= naive + halfWidth;
    candidate++
  ) {
    if (candidate < 0) continue;
    if (centsToWireFraction(candidate) !== wireFraction) continue;
    candidates.push(candidate);
  }

  if (candidates.length === 0) return naive;

  const wholeDollarNearNaive = candidates.find(
    (candidate) => candidate % 100 === 0 && Math.abs(candidate - naive) <= 2,
  );
  if (wholeDollarNearNaive !== undefined) return wholeDollarNearNaive;

  const roundedCenter = Math.round(exactCenter);
  const atCenter = candidates.find((candidate) => candidate === roundedCenter);
  if (atCenter !== undefined) return atCenter;

  return naive;
};
